import React, { useState, useEffect, useRef } from "react";
import styles from "./TicketUploadReview.module.scss";
import TNButton from "../../../reusable-components/TNButton";
import {
  errorAlertAlt,
  modalMessages,
  notificationMessages,
  successNotificationStyle,
  ticketReviewConstants,
} from "./constants";
import { ButtonShape, ButtonSize } from "../../../enums/ButtonAttributesEnums";
import Form from "../../Form";
import MainFileView from "./MainFileViewer";
import InputField from "../../../reusable-components/TNInputField";
import { InputfieldTypes } from "../../../enums/InputFieldAttributesEnums";
import { Progress, notification, Modal, message } from "antd";
import PDFPreview from "../../../reusable-components/PDFPreview";
import TicketUploadServices from "../../../services/ticketUpload.service";
import { useHistory, useLocation } from "react-router-dom";
import AddTicketDragger from "../TicketUploadDrawer/AddTicketDragger";
import { FileTypes } from "../../../enums/FileUploadEnums";
import { addTicketConstantStatus } from "../TicketUploadDrawer/AddTicketForm/constants";
import moment from "moment";
import { DateFormat } from "../../../enums/dateTimeFormat";
import AllApprovedModal from "./AllApprovedModal";
import SuccessIcon from "../../../assets/images/accept-icon.svg";
import ErrorAlert from "../../../assets/images/alert-triangle.svg";
import WarningAlert from "../../../assets/images/orange-alert.svg";
import ConditionalRender from "../../../reusable-components/ConditionalRender";
import TNLoader from "../../../reusable-components/TNLoader";
import { storageKeys } from "../../../constants/storageKeys";
import RolesServices from "../../../services/roles.services";
import { appRoutes } from "../../../constants/appRoutes";

const TicketUploadReview = () => {
  const location = useLocation();
  const navigate = useHistory();
  const pathLeadId = location.pathname.split("/")[3];
  const wrapperRef = useRef<HTMLDivElement>(null);

  //User Id Details
  const userDetails = localStorage.getItem("userDetails");
  const userDetailsJson = JSON.parse(userDetails);
  const userRoleInfo = userDetailsJson?.[storageKeys?.USER_ROLE_KEY];
  const userId = userDetailsJson?.["id"];
  const userRoleProfileInfo = userDetailsJson?.[storageKeys.USER_PROFILE_KEY];

  const { FetchUserRoleDetails } = RolesServices();
  const { data: roleDetails } = FetchUserRoleDetails(userRoleInfo);

  const {
    FetchTicketDetails,
    SaveSubmitForDataInput,
    ReplaceTicket,
    ReplaceFile,
  } = TicketUploadServices();
  const {
    data: ticketDetail = {},
    refetch,
    isPending,
    isLoading,
    isRefetching,
  } = FetchTicketDetails({ id: pathLeadId });
  const {
    mutateAsync: saveSubmitForDataInput,
    isPending: isDataSubmissionPending,
  } = SaveSubmitForDataInput();
  const { mutateAsync: replaceTicket, isPending: isReplacingTicketPending } =
    ReplaceTicket();
  const { mutateAsync: replaceFile, isPending: isReplacingFilePending } =
    ReplaceFile();
  const ticketsMoved = Number(ticketDetail?.data?.ticketsMoved);
  const totalTickets = Number(ticketDetail?.data?.totalTickets);
  const ticketProcessingAccess = !!userRoleProfileInfo
    ? roleDetails?.[0]?.otherData?.[4]?.multiPannel?.[1]?.selectedOptions
        ?.length > 0
    : true;
  const [isReplaceTktError, setIsReplaceTktError] = useState(false);
  const [activeTicket, setActiveTicket] = useState(
    ticketDetail?.data?.tickets?.[0]
  );
  const [checked, setCheckedValue] = useState(false);

  const [isCustomerWarningModalOpen, setCustomerWarningModalOpen] =
    useState(false);
  const handleWarningModalOpen = () => setCustomerWarningModalOpen(true);
  const handleWarningModalClose = () => setCustomerWarningModalOpen(false);

  const [isReplaceTicketModalOpen, setReplaceTicketModalOpen] = useState(false);
  const handleReplaceTicketModalOpen = () => setReplaceTicketModalOpen(true);
  const handleReplaceTicketModalClose = () => setReplaceTicketModalOpen(false);

  const [isReplaceFileModalOpen, setReplaceFileModalOpen] = useState(false);
  const handleReplaceFileModalOpen = () => setReplaceFileModalOpen(true);
  const handleReplaceFileModalClose = () => setReplaceFileModalOpen(false);

  const [isUploadDraggerVisible, setUploadDraggerVisible] = useState(false);
  const handleUploadDraggerOpen = () => setUploadDraggerVisible(true);
  const handleUploadDraggerClose = () => {
    setDocumentKey(null);
    setUploadDraggerVisible(false);
    setIsReplaceTktError(false);
  };

  const [isAllApprovedModalVisible, setIsAllApprovedModalVisible] =
    useState(false);
  const handleAllApproveModalOpen = () => setIsAllApprovedModalVisible(true);
  const handleAllApproveModalClose = () => setIsAllApprovedModalVisible(false);
  const removeReplaceTktError = () => setIsReplaceTktError(false);

  //Document Key
  const [documentKey, setDocumentKey] = useState(null);

  const dataInputSuccess = {
    message: ticketReviewConstants.SUCCESS,
    description: notificationMessages.success,
    icon: <img className={styles.successNotificationIcon} src={SuccessIcon} />,
    className: styles.successNotification,
    style: successNotificationStyle,
  };

  //Handle Data submission
  const handleDataSubmission = async () => {
    const payload = {
      uploadStatus: addTicketConstantStatus.submissionReviewStatus,
      updatedBy: userId,
      singlePdfTicket: activeTicket?.singlePdfTicket,
      ticketUploadPicture: activeTicket?.ticketUploadPicture,
      signedByCustomer: checked,
      id: activeTicket?.id,
    };
    await saveSubmitForDataInput(payload, {
      onSuccess: () => {
        notification.success(dataInputSuccess);
        setCheckedValue(false);
        refetch({ id: pathLeadId });
        handleWarningModalClose();
      },
    });
  };

  //Handle Replace Ticket
  const handleReplaceTicket = async () => {
    const payload = {
      ...activeTicket,
      ticketUploadPicture: documentKey,
    };
    await replaceTicket(payload, {
      onSuccess: () => {
        handleUploadDraggerClose();
        handleReplaceTicketModalClose();
        setCheckedValue(false);
        notification.success({ message: notificationMessages.replaceTkt });
      },
      onError: (err) => {
        message.error(err?.response?.message ?? err?.response?.data?.message);
        if (
          err?.response?.data?.message === notificationMessages.replaceTktError
        ) {
          setIsReplaceTktError(true);
        } else {
          message.error(err?.response?.data?.message);
        }
        console.error(err?.response);
      },
    });
    refetch({ id: pathLeadId });
  };

  //Handle Replace File
  const handleReplaceFile = async () => {
    const ticketIds = ticketDetail?.data?.tickets
      ?.map((item) => item.id)
      ?.filter(Boolean);
    const ticketPaths = ticketDetail?.data?.tickets
      ?.map((item) => item.singlePdfTicket)
      ?.filter(Boolean);
    const { singlePdfTicket, ticketIndex, ...basePayload } = activeTicket;
    const payload = {
      ...basePayload,
      ticketIds: ticketIds,
      ticketPaths: ticketPaths,
      ticketUploadPicture: documentKey,
    };
    await replaceFile(payload, {
      onSuccess: () => {
        notification.success({ message: notificationMessages.replaceFile });
        handleUploadDraggerClose();
        handleReplaceFileModalClose();
        setCheckedValue(false);
        refetch({ id: pathLeadId });
      },
      onError: (err) => {
        message.error(err?.response?.message);
        console.error(err?.response);
      },
    });
  };

  useEffect(() => {
    const sorted = ticketDetail?.data?.tickets
    ?.filter(
      (ticket) => ticket?.uploadStatus !== "Closed"
    )
    ?.sort((a, b) => a.ticketIndex - b.ticketIndex)
    setActiveTicket(sorted?.[0]);
  }, [ticketDetail?.data?.tickets]);

  useEffect(() => {
    refetch({ id: pathLeadId });
  }, [pathLeadId]);

  useEffect(() => {
    if (isLoading || isPending || isRefetching) {
      handleAllApproveModalClose();
    } else {
      if (ticketDetail?.data) {
        if (ticketDetail?.data?.allTicketsMoved) {
          handleAllApproveModalOpen();
        } else {
          handleAllApproveModalClose();
        }
      }
    }
  }, [ticketDetail?.data?.allTicketsMoved, isLoading, isPending, isRefetching]);

  useEffect(() => {
    // Store ticket state before page unload
    const handleBeforeUnload = () => {
      const allTicketsMoved =
        ticketDetail?.data?.ticketsMoved === ticketDetail?.data?.totalTickets &&
        ticketDetail?.data?.allTicketsMoved;
      sessionStorage.setItem(
        storageKeys.TICKET_REVIEW_REFRESH,
        allTicketsMoved.toString()
      );
    };

    // Check after component mounts if we need to redirect
    const wereAllTicketsMoved =
      sessionStorage.getItem(storageKeys.TICKET_REVIEW_REFRESH) === "true";

    if (wereAllTicketsMoved) {
      sessionStorage.removeItem(storageKeys.TICKET_REVIEW_REFRESH);
      navigate?.push(appRoutes.VIEW_TICKET_UPLOAD_LISTING);
    }

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [ticketDetail?.data]);

  return (
    <div key={ticketDetail?.id}>
      <TNLoader
        isLoading={
          isDataSubmissionPending ||
          isReplacingFilePending ||
          isReplacingTicketPending ||
          isRefetching ||
          isLoading ||
          isPending
        }
      />
      <Modal
        width={modalMessages.uploadDraggerModalWidth}
        visible={isUploadDraggerVisible}
        title={
          isReplaceTicketModalOpen
            ? modalMessages.uploadTitle
            : modalMessages.uploadFile
        }
        onCancel={handleUploadDraggerClose}
        onOk={
          isReplaceTicketModalOpen ? handleReplaceTicket : handleReplaceFile
        }
        okButtonProps={{
          disabled: !!!documentKey,
        }}
        destroyOnClose={true}
        okText={modalMessages.uploadButtonConfirmLabel}
        cancelText={modalMessages.uploadButtonCancelLabel}
      >
        <AddTicketDragger
          onFilesUploaded={() => {}}
          docType={""}
          filesUploaded={[]}
          maxFileCount={1}
          allowedFileTypes={FileTypes.PDF}
          setDocumentKey={setDocumentKey}
          clearReplaceTktError={removeReplaceTktError}
          largeSizeFile={isReplaceTicketModalOpen ? false : true}
        />
        {isReplaceTktError && (
          <div className={styles.replaceTktError}>
            <img src={ErrorAlert} alt={errorAlertAlt} />
            <div className={styles.singlePageError}>
              {notificationMessages.replaceTktError}
            </div>
          </div>
        )}
      </Modal>
      <Modal
        width={modalMessages.uploadDraggerModalWidth}
        visible={isReplaceFileModalOpen}
        title={modalMessages.replaceFile}
        onCancel={handleReplaceFileModalClose}
        onOk={handleUploadDraggerOpen}
        okText={modalMessages.replaceButtonConfirmLabel}
        cancelText={modalMessages.replaceButtonCancelLabel}
      >
        {modalMessages.replaceFileContent}
      </Modal>
      <Modal
        width={modalMessages.uploadDraggerModalWidth}
        visible={isReplaceTicketModalOpen}
        title={modalMessages.replaceTicketTitle}
        onCancel={handleReplaceTicketModalClose}
        onOk={handleUploadDraggerOpen}
        okText={modalMessages.replaceButtonConfirmLabel}
        cancelText={modalMessages.replaceButtonCancelLabel}
      >
        {modalMessages.replaceTicketContent}
      </Modal>
      <Modal
        visible={isCustomerWarningModalOpen}
        footer={null}
        className={styles.customerWarningModal}
        centered
        onCancel={handleWarningModalClose}
        destroyOnClose
      >
        <div className={styles.customerWarningModalContent}>
          <img className={styles.customerWarningModalIcon} src={WarningAlert} />
          <h4>{modalMessages.customerConfirmation}</h4>
          <div className={styles.customerWarningModalButtons}>
            <TNButton
              size={ButtonSize.LARGE}
              shape={ButtonShape.DEFAULT}
              className={modalMessages.buttonClass}
              text={modalMessages.cancelWithoutSignature}
              onClick={handleWarningModalClose}
            />
            <TNButton
              size={ButtonSize.LARGE}
              shape={ButtonShape.DEFAULT}
              className={modalMessages.buttonClass}
              text={modalMessages.confirmWithoutSignature}
              onClick={handleDataSubmission}
            />
          </div>
        </div>
      </Modal>
      <ConditionalRender
        visible={
          ticketDetail?.data?.ticketsMoved === ticketDetail?.data?.totalTickets
        }
      >
        <AllApprovedModal
          isOpen={isAllApprovedModalVisible}
          handleClose={handleAllApproveModalClose}
          hasProcessingAccess={ticketProcessingAccess}
        />
      </ConditionalRender>
      <Form>
        {() => {
          return (
            <>
              <div className={styles.headerSection}>
                <div className={styles.fileDetailsLabel}>
                  <span className={styles.fileDetailsName}>
                    {ticketDetail?.data?.tickets?.[0]?.ticketUploadPicture
                      ?.split("/")?.[1]
                      ?.slice(0, -4) ?? ""}
                  </span>
                  <span className={styles.fileDetailsTimestamp}>
                    {moment(ticketDetail?.data?.createdAt)?.format(
                      DateFormat.MM_DD_YYYY_TIME
                    )}
                  </span>
                </div>
                <TNButton
                  shape={ButtonShape.DEFAULT}
                  size={ButtonSize.LARGE}
                  text={ticketReviewConstants.REPLACE_FILE_BTN}
                  onClick={handleReplaceFileModalOpen}
                  isRefetching={isRefetching}
                />
              </div>
              <div className={styles.ticketUploadWrapper}>
                <div className={styles.ticketUploadContainer}>
                  <div className={styles.viewerSection}>
                    <div ref={wrapperRef} className={styles.mainFileView}>
                      <MainFileView
                        url={
                          activeTicket?.presignedTicketUrl ?? ticketDetail?.data?.tickets?.[0]?.presignedTicketUrl
                        }
                        wrapperRef={wrapperRef}
                      />
                    </div>
                    <div className={styles.bottomPanel}>
                      <div className={styles.rightSection}>
                        <div className={styles.checkboxContainer}>
                          <div className={styles.checkboxField}>
                            <InputField
                              type={InputfieldTypes.CHECKBOX}
                              name={ticketReviewConstants.SIGNED_CHECKBOX_NAME}
                              value={checked}
                              onChange={(val) => setCheckedValue(val)}
                            />
                          </div>
                          <span className={styles.checkboxTitle}>
                            {ticketReviewConstants.SIGNED_BY_CUSTOMER}
                          </span>
                        </div>
                        <TNButton
                          className={
                            ticketReviewConstants.REPLACE_TKT_BTN_CLASS
                          }
                          shape={ButtonShape.DEFAULT}
                          size={ButtonSize.LARGE}
                          text={ticketReviewConstants.REPLACE_TKT_BTN}
                          onClick={handleReplaceTicketModalOpen}
                        />
                        <TNButton
                          className={ticketReviewConstants.SUBMIT_TKT_BTN_CLASS}
                          shape={ButtonShape.DEFAULT}
                          size={ButtonSize.LARGE}
                          text={ticketReviewConstants.SUBMIT_TKT_BTN}
                          onClick={() => {
                            checked
                              ? handleDataSubmission()
                              : handleWarningModalOpen();
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className={styles.sideListView}>
                    <div className={styles.sideListHeader}>
                      <div className={styles.sideListLabel}>
                        {ticketReviewConstants.TICKET_PROGRESS_LABEL}
                      </div>
                      <Progress
                        percent={(ticketsMoved / totalTickets) * 100}
                        strokeColor={
                          ticketReviewConstants.PROGRESS_STROKE_COLOR
                        }
                        status={ticketReviewConstants.PROGRESS_STATUS}
                        showInfo={false}
                      />
                      <div className={styles.sideListCount}>
                        {!!totalTickets && `${ticketsMoved}/${totalTickets}`}
                      </div>
                    </div>
                    <div className={styles.pdfPreviewList}>
                      {ticketDetail?.data?.tickets?.length > 0 &&
                        ticketDetail?.data?.tickets
                          ?.filter(
                            (ticket) => ticket?.uploadStatus !== "Closed"
                          )
                          ?.sort((a, b) => a.ticketIndex - b.ticketIndex)
                          ?.map((ticket, index) => {
                            return (
                              <div
                                className={styles.pdfPreviewWrapper}
                                onClick={() => {
                                  setActiveTicket(ticket);
                                  setCheckedValue(false);
                                }}
                              >
                                <span className={styles.previewIndex}>
                                  {index + 1}
                                </span>
                                <PDFPreview
                                  url={ticket?.presignedTicketUrl}
                                  selected={ticket?.id === activeTicket?.id}
                                />
                              </div>
                            );
                          })}
                    </div>
                  </div>
                </div>
              </div>
            </>
          );
        }}
      </Form>
    </div>
  );
};

export default TicketUploadReview;
